import React, { useEffect, useRef, useState } from 'react';
import RejuvenationProcAnalyzer from './components/RejuvenationProcAnalyzer';
import ItemSetCalculator from './components/ItemSetCalculator';
import StatValueAnalyzer from './components/StatValueAnalyzer';
import HealerTrinketAnalyzer from './components/HealerTrinketAnalyzer';
import ConsumesChecker from './components/ConsumesChecker';
import RestoIdolAnalyzer from './components/RestoIdolAnalyzer';
import CrystalSpireAnalyzer from './components/CrystalSpireAnalyzer';

import { navigate, usePathname, useSharedReportView } from './routing';

type AppTab = 'statvalues' | 'trinkets' | 'consumes' | 'idols' | 'spire' | 'procs' | 'itemsets';

const TAB_PATHS: Record<AppTab, string> = {
    statvalues: '/stat-values',
    trinkets: '/healer-trinkets',
    consumes: '/consumes',
    idols: '/tree-of-life',
    spire: '/crystal-spire',
    procs: '/rejuvenation-procs',
    itemsets: '/t3-sets',
};

const DEFAULT_TAB: AppTab = 'consumes';

const MAIN_TABS: Array<{ tab: AppTab; label: string }> = [
    { tab: 'consumes', label: 'Consumes' },
    { tab: 'statvalues', label: 'Stat Values' },
];

type NavMenuId = 'items' | 'classic';

const NAV_MENUS: Array<{ id: NavMenuId; label: string; tabs: Array<{ tab: AppTab; label: string }> }> = [
    {
        id: 'items',
        label: 'Items',
        tabs: [
            { tab: 'trinkets', label: 'Healer Trinkets' },
            { tab: 'idols', label: 'Tree of Life (idols)' },
            { tab: 'spire', label: 'Crystal Spire of Karabor' },
        ],
    },
    {
        id: 'classic',
        label: 'Classic Era',
        tabs: [
            { tab: 'procs', label: 'Rejuvenation Procs' },
            { tab: 'itemsets', label: 'T3 Set Calculator' },
        ],
    },
];

/** `/<tab>` or `/<tab>/report/<id>`; null when the path matches no tab. */
function parseRoute(pathname: string): { tab: AppTab; reportId: string | null } | null {
    const [tabSegment, kind, id] = pathname.split('/').filter((segment) => segment.length > 0);
    const tab = (Object.keys(TAB_PATHS) as AppTab[]).find((key) => TAB_PATHS[key] === `/${tabSegment}`);
    if (!tab) {
        return null;
    }
    return { tab, reportId: kind === 'report' && id ? id : null };
}

function App() {
    const pathname = usePathname();
    const route = parseRoute(pathname);
    const activeTab = route?.tab ?? DEFAULT_TAB;
    const reportId = route?.reportId ?? null;
    const sharedReportView = useSharedReportView();
    const [openMenu, setOpenMenu] = useState<NavMenuId | null>(null);
    const navRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (!openMenu) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(event.target as Node)) {
                setOpenMenu(null);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpenMenu(null);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [openMenu]);

    useEffect(() => {
        if (!route) {
            navigate(TAB_PATHS[DEFAULT_TAB], { replace: true });
        }
    }, [route]);

    /** Real links (middle-click / ctrl-click open a new tab); plain clicks route in place. */
    const tabLinkProps = (tab: AppTab) => ({
        href: TAB_PATHS[tab],
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            event.preventDefault();
            navigate(TAB_PATHS[tab]);
            setOpenMenu(null);
        },
    });

    const tabClass = (tab: AppTab) => `app-tab${activeTab === tab ? ' is-active' : ''}`;

    return (
        <>
            {!sharedReportView && (
                <header className="app-header">
                    <div className="app-header-inner">
                        <a className="app-brand" {...tabLinkProps(DEFAULT_TAB)}>
                            <img className="app-brand-icon" src="/favicon.svg" alt="" />
                            <span className="app-brand-name">WoW Calculators</span>
                            <span className="app-brand-tag">Warcraft Logs tools</span>
                        </a>

                        <nav className="app-nav" aria-label="Calculators" ref={navRef}>
                            {MAIN_TABS.map(({ tab, label }) => (
                                <a
                                    key={tab}
                                    className={tabClass(tab)}
                                    aria-current={activeTab === tab ? 'page' : undefined}
                                    {...tabLinkProps(tab)}
                                >
                                    {label}
                                </a>
                            ))}
                            <span className="app-nav-divider" aria-hidden="true" />
                            {NAV_MENUS.map((menu) => {
                                const activeEntry = menu.tabs.find((entry) => entry.tab === activeTab);
                                const open = openMenu === menu.id;
                                return (
                                    <div className="nav-more" key={menu.id}>
                                        <button
                                            type="button"
                                            className={`app-tab${activeEntry ? ' is-active' : ''}${open ? ' is-open' : ''}`}
                                            aria-haspopup="menu"
                                            aria-expanded={open}
                                            onClick={() => setOpenMenu(open ? null : menu.id)}
                                        >
                                            {activeEntry?.label ?? menu.label}
                                            <span className="app-tab-caret" aria-hidden="true">
                                                ▾
                                            </span>
                                        </button>
                                        {open && (
                                            <div className="nav-more-menu" role="menu">
                                                <p className="nav-more-label">{menu.label}</p>
                                                {menu.tabs.map(({ tab, label }) => (
                                                    <a
                                                        key={tab}
                                                        role="menuitem"
                                                        className={activeTab === tab ? 'active' : ''}
                                                        {...tabLinkProps(tab)}
                                                    >
                                                        {label}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </nav>
                    </div>
                </header>
            )}

            <main className="container">
                {activeTab === 'statvalues' && (
                    <StatValueAnalyzer basePath={TAB_PATHS.statvalues} reportId={reportId} />
                )}

                {activeTab === 'trinkets' && (
                    <HealerTrinketAnalyzer basePath={TAB_PATHS.trinkets} reportId={reportId} />
                )}

                {activeTab === 'consumes' && <ConsumesChecker basePath={TAB_PATHS.consumes} reportId={reportId} />}

                {activeTab === 'idols' && <RestoIdolAnalyzer basePath={TAB_PATHS.idols} reportId={reportId} />}

                {activeTab === 'spire' && <CrystalSpireAnalyzer basePath={TAB_PATHS.spire} reportId={reportId} />}

                {activeTab === 'procs' && <RejuvenationProcAnalyzer />}

                {activeTab === 'itemsets' && <ItemSetCalculator />}
            </main>
        </>
    );
}

export default App;
